import Phaser from 'phaser';
import { SLICE, TEX } from './assets';
import { fontDisplay, MIN_TEXT_PX, textInk, textResolution } from './theme';
import { addText } from './widgets';
import { ensureGrainPlates, frameInset, grainSample, grainSeed, grainVariant, nextGrainSalt, type GrainWindow } from './woodGrain';

/**
 * Carved wood from the panel nine-slice, with the turn-banner grain on the face.
 * Short planks scale the carving the same way the turn banner does. Taller
 * panels keep the native frame and stretch only the middle, so the face stays
 * a readable inset instead of a blown-up rim.
 */
export function woodPanelKey(scene: Phaser.Scene, width: number, height: number, seed?: number): string | null {
  if (!scene.textures.exists(TEX.panelWood)) return null;
  const ratio = Math.max(1, textResolution());
  const w = Math.max(8, Math.round(width));
  const h = Math.max(8, Math.round(height));
  const chosen = seed ?? grainSeed(w, h, w, h);
  const sample = grainSample(chosen, Math.round(w * ratio), Math.round(h * ratio));
  const key = `turn-wood-${w}x${h}@${ratio}-${grainVariant(sample)}`;
  if (scene.textures.exists(key)) return key;
  const texture = scene.textures.get(TEX.panelWood);
  const source = texture.getSourceImage() as CanvasImageSource & { width?: number; height?: number };
  const frame = texture.get();
  const srcW = source.width || frame.width;
  const srcH = source.height || frame.height;
  if (!srcW || !srcH) return null;
  const left = SLICE.wood.left;
  const right = SLICE.wood.right;
  const top = SLICE.wood.top;
  const bot = SLICE.wood.bottom;
  const srcMidW = srcW - left - right;
  const srcMidH = srcH - top - bot;
  if (srcMidW <= 0 || srcMidH <= 0) return null;
  const canvas = scene.textures.createCanvas(key, Math.round(w * ratio), Math.round(h * ratio));
  if (!canvas) return null;
  const ctx = canvas.getContext();
  const sy = Math.min(1, h / srcH);
  const capL = Math.min(w / 3, Math.max(8, Math.round(left * sy)));
  const capR = Math.min(w / 3, Math.max(8, Math.round(right * sy)));
  const midW = Math.max(1, w - capL - capR);
  const topH = Math.max(1, Math.round(top * sy));
  const botH = Math.max(1, Math.round(bot * sy));
  const midH = h - topH - botH;
  if (midH <= 0) {
    scene.textures.remove(key);
    return null;
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  const blit = (sx: number, sy0: number, sw: number, sh: number, dx: number, dy: number, dw: number, dh: number) => {
    ctx.drawImage(source, sx, sy0, sw, sh, dx * ratio, dy * ratio, dw * ratio, dh * ratio);
  };
  blit(0, 0, left, top, 0, 0, capL, topH);
  blit(left, 0, srcMidW, top, capL, 0, midW, topH);
  blit(srcW - right, 0, right, top, capL + midW, 0, capR, topH);
  blit(0, top, left, srcMidH, 0, topH, capL, midH);
  blit(left, top, srcMidW, srcMidH, capL, topH, midW, midH);
  blit(srcW - right, top, right, srcMidH, capL + midW, topH, capR, midH);
  blit(0, srcH - bot, left, bot, 0, topH + midH, capL, botH);
  blit(left, srcH - bot, srcMidW, bot, capL, topH + midH, midW, botH);
  blit(srcW - right, srcH - bot, right, bot, capL + midW, topH + midH, capR, botH);
  layWoodGrain(scene, ctx, w, h, ratio, sample, { left: capL, right: capR, top: topH, bottom: botH });
  canvas.refresh();
  return key;
}

/** How strongly the plate shifts the face. Kept low so cream type still reads. */
const GRAIN_GAIN = 1.05;
/**
 * Source pixels from the outer edge to the inner lip of the carve.
 * Grain fills everything inside this, so the flat face meets the frame.
 */
const FRAME_LIP = { left: 44, right: 42, top: 40, bottom: 34 };

function clampByte(value: number): number {
  return value < 0 ? 0 : value > 255 ? 255 : value;
}

/**
 * Lay one grain window on the face. The carved rim is left as the nine-slice drew it.
 * Average color stays put; only the plate's deviation is mixed in.
 */
function layWoodGrain(
  scene: Phaser.Scene,
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  ratio: number,
  sample: GrainWindow,
  rim: { left: number; right: number; top: number; bottom: number },
): void {
  if (typeof document === 'undefined') return;
  const plates = ensureGrainPlates(scene);
  const plate = plates?.[sample.plate];
  if (!plate) return;
  const gw = Math.round(w * ratio);
  const gh = Math.round(h * ratio);
  const grainLen = sample.vertical ? gh : gw;
  const grainCross = sample.vertical ? gw : gh;
  const strip = document.createElement('canvas');
  strip.width = Math.max(1, grainLen);
  strip.height = Math.max(1, grainCross);
  const stripCtx = strip.getContext('2d');
  if (!stripCtx) return;
  stripCtx.imageSmoothingEnabled = true;
  stripCtx.imageSmoothingQuality = 'high';
  stripCtx.save();
  stripCtx.translate(sample.flipAlong ? grainLen : 0, sample.flipCross ? grainCross : 0);
  stripCtx.scale(sample.flipAlong ? -1 : 1, sample.flipCross ? -1 : 1);
  stripCtx.drawImage(plate.canvas, sample.sx, sample.sy, sample.sw, sample.sh, 0, 0, grainLen, grainCross);
  stripCtx.restore();

  const face = document.createElement('canvas');
  face.width = gw;
  face.height = gh;
  const faceCtx = face.getContext('2d');
  if (!faceCtx) return;
  if (sample.vertical) {
    // Strip x (the grain) becomes panel y. Strip y becomes panel x.
    faceCtx.setTransform(0, 1, 1, 0, 0, 0);
  }
  faceCtx.drawImage(strip, 0, 0);
  faceCtx.setTransform(1, 0, 0, 1, 0, 0);

  let grain: ImageData;
  let base: ImageData;
  try {
    grain = faceCtx.getImageData(0, 0, gw, gh);
    base = ctx.getImageData(0, 0, gw, gh);
  } catch {
    return;
  }
  const data = base.data;
  const src = grain.data;
  const [meanR, meanG, meanB] = plate.mean;
  const insetL = Math.round(frameInset(rim.left, SLICE.wood.left, FRAME_LIP.left) * ratio);
  const insetR = Math.round(frameInset(rim.right, SLICE.wood.right, FRAME_LIP.right) * ratio);
  const insetT = Math.round(frameInset(rim.top, SLICE.wood.top, FRAME_LIP.top) * ratio);
  const insetB = Math.round(frameInset(rim.bottom, SLICE.wood.bottom, FRAME_LIP.bottom) * ratio);
  for (let y = insetT; y < gh - insetB; y++) {
    for (let x = insetL; x < gw - insetR; x++) {
      const i = (y * gw + x) * 4;
      if (data[i + 3] < 180) continue;
      data[i] = clampByte(data[i] + (src[i] - meanR) * GRAIN_GAIN);
      data[i + 1] = clampByte(data[i + 1] + (src[i + 1] - meanG) * GRAIN_GAIN);
      data[i + 2] = clampByte(data[i + 2] + (src[i + 2] - meanB) * GRAIN_GAIN);
    }
  }
  ctx.putImageData(base, 0, 0);
}

export type WoodFace = Phaser.GameObjects.Image | Phaser.GameObjects.NineSlice;

/** One carved plank. `x` and `y` are the top-left, matching `addPanel`. */
export function addWoodPanel(
  scene: Phaser.Scene,
  x: number,
  y: number,
  w: number,
  h: number,
  depth = 5,
): WoodFace {
  const key = woodPanelKey(scene, w, h, grainSeed(x, y, w, h));
  if (key) {
    return scene.add.image(x + w / 2, y + h / 2, key).setDisplaySize(w, h).setDepth(depth);
  }
  return scene.add
    .nineslice(x + w / 2, y + h / 2, TEX.panelWood, undefined, w, h, SLICE.wood.left, SLICE.wood.right, SLICE.wood.top, SLICE.wood.bottom)
    .setDepth(depth);
}

/** Leaf tips at the ends of a banner. */
export function addLeafTips(
  scene: Phaser.Scene,
  cx: number,
  cy: number,
  w: number,
  h: number,
  depth = 6,
): Phaser.GameObjects.Image[] {
  if (!scene.textures.exists(TEX.vfxLeaf)) return [];
  const leafH = Math.min(40, Math.round(h * 0.55));
  const leafW = Math.round(leafH * 0.78);
  const inset = Math.min(28, Math.max(8, Math.round(w * 0.08)));
  return [
    scene.add.image(cx - w / 2 + inset, cy, TEX.vfxLeaf).setDisplaySize(leafW, leafH).setAngle(-38).setDepth(depth),
    scene.add.image(cx + w / 2 - inset, cy, TEX.vfxLeaf).setDisplaySize(leafW, leafH).setAngle(28).setFlipX(true).setDepth(depth),
  ];
}

/** Cream Fredoka with the dark outline used on every wood face. */
export function woodType(size: string): Phaser.Types.GameObjects.Text.TextStyle {
  const parsed = Number.parseInt(size, 10);
  const px = Math.max(MIN_TEXT_PX, Number.isFinite(parsed) ? parsed : MIN_TEXT_PX);
  const stroke = Math.min(5, Math.max(3, Math.round(px / 10)));
  return {
    fontFamily: fontDisplay,
    fontSize: `${px}px`,
    fontStyle: '700',
    color: '#FFF6E4',
    stroke: textInk.parchment,
    strokeThickness: stroke,
    padding: { x: 4, y: 4 },
    shadow: {
      offsetX: 0,
      offsetY: 2,
      color: textInk.parchment,
      blur: 0,
      fill: true,
      stroke: false,
    },
    resolution: textResolution(),
  };
}

/** Banner plank, leaf tips, and a centered label. Returns the label. */
export function addWoodBanner(
  scene: Phaser.Scene,
  cx: number,
  cy: number,
  w: number,
  h: number,
  label: string,
  fontSize = '32px',
  depth = 6,
): Phaser.GameObjects.Text {
  addWoodPanel(scene, cx - w / 2, cy - h / 2, w, h, depth);
  addLeafTips(scene, cx, cy, w, h, depth + 1);
  return addText(scene, cx, cy, label, woodType(fontSize)).setOrigin(0.5).setDepth(depth + 2);
}

/**
 * Turn-sweep plank. Children sit at local 0,0 so the sweep container can
 * carry them. Leaf size matches the 88px banner.
 */
export function carvedPlankParts(scene: Phaser.Scene, w: number, h: number): Phaser.GameObjects.GameObject[] {
  const parts: Phaser.GameObjects.GameObject[] = [];
  const key = woodPanelKey(scene, w, h, grainSeed(nextGrainSalt() * 80, 12, w, h));
  if (key) {
    parts.push(scene.add.image(0, 0, key).setDisplaySize(w, h).setName('turn-wood'));
  } else {
    const g = scene.add.graphics();
    const radius = Math.min(22, Math.round(h / 2));
    g.fillStyle(0x3a2414, 1);
    g.fillRoundedRect(-w / 2, -h / 2, w, h, radius);
    g.fillStyle(0x6b3e22, 1);
    g.fillRoundedRect(-w / 2 + 6, -h / 2 + 6, w - 12, h - 12, Math.max(8, radius - 4));
    g.lineStyle(3, 0x2a160c, 1);
    g.strokeRoundedRect(-w / 2 + 3, -h / 2 + 3, w - 6, h - 6, Math.max(6, radius - 2));
    parts.push(g);
  }
  if (scene.textures.exists(TEX.vfxLeaf)) {
    const leafH = Math.min(40, Math.round(h * 0.5));
    const leafW = Math.round(leafH * 0.78);
    parts.push(
      scene.add.image(-w / 2 + 28, 0, TEX.vfxLeaf).setDisplaySize(leafW, leafH).setAngle(-38),
      scene.add.image(w / 2 - 28, 0, TEX.vfxLeaf).setDisplaySize(leafW, leafH).setAngle(28).setFlipX(true),
    );
  }
  return parts;
}
